import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Home } from 'lucide-react';
import { Button } from '../../../../../components/ui/Button';
import { Modal } from '../../../../../components/ui/Modal';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { fetchPickupPoints } from '../../../../../features/pickupPoints/pickupPointsThunks';
import { scheduleLabel } from '../../../../../features/pickupPoints/pickupPointsUtils';
import {
  redirectToPickupPoint,
  revertPickupPoint,
} from '../../../../../features/orderDetails/orderDetailsThunks';
import { getFullAddress } from '../../../../../features/orderDetails/orderUtils';

const PickupPointDialog = ({ onClose, order }) => {
  const dispatch = useDispatch();
  const { items, loading, loaded, error: loadError } = useSelector((state) => state.pickupPoints);
  const delivery = order.delivery;
  const current = delivery?.pickup_point;

  // Read once, at mount: a refetch of the order while it is open must not put
  // back the current point over the one just picked.
  const [pickupPointId, setPickupPointId] = useState(current?.pickup_point_id || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!loaded) dispatch(fetchPickupPoints());
  }, [dispatch, loaded]);

  const options = items.map((point) => ({
    value: point.id,
    label: `${point.name} · ${getFullAddress(point.address)}`,
  }));
  const chosen = items.find((point) => point.id === pickupPointId);
  const emptyLabel = loading ? 'Cargando puntos de retiro...' : 'No tienes puntos de retiro';

  const run = async (thunk) => {
    setSaving(true);
    setError(null);
    const action = await dispatch(thunk);
    setSaving(false);
    if (action.meta.requestStatus === 'fulfilled') onClose();
    else setError(action.payload);
  };

  const handleSave = () =>
    run(redirectToPickupPoint({ orderId: order.id, deliveryId: delivery.id, pickupPointId }));
  const handleRevert = () => run(revertPickupPoint({ orderId: order.id, deliveryId: delivery.id }));

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={current ? 'Cambiar punto de retiro' : 'Entregar en punto de retiro'}
      busy={saving}
      error={error || loadError}
      errorTitle="No se pudo cambiar el destino"
      testId="pickup-point-modal"
    >
      <div className="space-y-5">
        <p className="text-sm text-text-secondary">
          El pedido se enviará al courier con la dirección del punto de retiro. La dirección del
          cliente se conserva. Cuando el courier entregue quedará <b>Dejado en hub</b>, al
          escanearlo el punto pasará a <b>Recibido en hub</b>, y se cerrará como Entregado
          cuando el punto se lo entregue al cliente.
        </p>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1.5">
            Punto de retiro
          </label>
          <FilterableSelect
            variant="form"
            value={pickupPointId}
            onChange={setPickupPointId}
            options={options}
            placeholder={options.length ? 'Selecciona un punto de retiro' : emptyLabel}
            disabled={options.length === 0}
          />
          {chosen && (
            <p className="text-xs text-text-muted mt-2">Recepción: {scheduleLabel(chosen)}</p>
          )}
        </div>

        <div className="rounded-xl bg-ninja-gray/50 dark:bg-[#111] border border-border p-3 text-sm">
          <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-text-muted mb-1">
            <Home size={12} /> Dirección del cliente
          </span>
          <span className="text-ninja-dark dark:text-white">
            {getFullAddress(current ? current.original_shipping_address : delivery?.shipping_address)}
          </span>
        </div>

        <div className="flex flex-col-reverse sm:flex-row items-center sm:justify-between gap-3 pt-1">
          <div>
            {current && (
              <Button variant="subtle" pill onClick={handleRevert} disabled={saving}>
                <Home size={14} /> Volver al domicilio del cliente
              </Button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="subtle" pill onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              pill
              onClick={handleSave}
              loading={saving}
              loadingLabel="Cambiando..."
              disabled={!pickupPointId || pickupPointId === current?.pickup_point_id}
            >
              {current ? 'Cambiar punto' : 'Enviar a punto de retiro'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

/**
 * Redirects a shipment to one of the operator's pickup points, or back to the
 * customer's door. Only before the courier has it; the menu item says so.
 *
 * Mounted only while open: each opening starts from the order as it is then.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object} props.order
 */
const PickupPointModal = (props) => (props.isOpen ? <PickupPointDialog {...props} /> : null);

export default PickupPointModal;
